import React from 'react';
import { motion } from 'framer-motion';
import { NavLink, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
// Custom components
import { HSeparator } from 'components/separator/Separator';
import DefaultAuth from 'layouts/auth/Default';
// Assets
import illustration from 'assets/img/auth/bg-auth.jpg';
import { MdArrowBack } from 'react-icons/md';
import toast from 'react-hot-toast';
import { createAPIEndPoint } from 'config/api/api';

function ResetPassword() {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);

  // Chakra color mode
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = 'gray.400';

  const handleResetPassword = async (values) => {
    try {
      setLoading(true);
      const data = {
        otp: values.otp,
        new_password: values.newPassword,
      };
      const response = await createAPIEndPoint('verify-otp').create(data);
      toast.success(response.data.message || 'Password updated successfully');
      navigate('/auth/sign-in');
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          'Failed to reset password. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      otp: '',
      newPassword: '',
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .required('OTP is required')
        .min(6, 'OTP must be 6 digits'),
      newPassword: Yup.string()
        .required('New Password is required')
        .min(6, 'Password must be at least 6 characters'),
    }),
    onSubmit: (values) => {
      handleResetPassword(values);
    },
  });

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        direction="column"
        align="center"
        justify="center"
        minH="100vh"
        w="100%"
        px={{ base: 4, md: 10 }}
      >
        <Box
          w={{ base: '100%', sm: '95%', md: '480px', lg: '500px' }}
          p={{ base: 6, sm: 8 }}
          borderRadius="xl"
          boxShadow="xl"
          bg="white"
        >
          <Box me="auto">
            <Heading
              color={textColor}
              fontSize={{ base: '26px', md: '28px', lg: '30px' }}
              mb="10px"
            >
              Reset Password
            </Heading>
            <Text
              mb="16px"
              ms="4px"
              color={textColorSecondary}
              fontWeight="400"
              fontSize="md"
            >
              Enter OTP and your new password to reset your password.
            </Text>
          </Box>
          <Flex
            zIndex="2"
            direction="column"
            w={{ base: '100%', md: '420px' }}
            maxW="100%"
            background="transparent"
            borderRadius="15px"
            mx={{ base: 'auto', lg: 'unset' }}
            me="auto"
            mb={{ base: '20px', md: 'auto' }}
          >
            <Flex align="center" mb="25px">
              <HSeparator />
            </Flex>
            <form onSubmit={formik.handleSubmit}>
              <FormControl isInvalid={formik.touched.otp && formik.errors.otp}>
                <FormLabel
                  display="flex"
                  ms="4px"
                  fontSize="sm"
                  fontWeight="500"
                  color={textColor}
                  mb="8px"
                >
                  OTP<Text color="red.500">*</Text>
                </FormLabel>
                <Input
                  name="otp"
                  variant="auth"
                  fontSize="sm"
                  type="text"
                  placeholder="Enter OTP"
                  mb="8px"
                  fontWeight="500"
                  size="lg"
                  value={formik.values.otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // Remove non-numeric characters
                    if (value.length <= 6) {
                      formik.setFieldValue('otp', value);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  borderColor={
                    formik.errors.otp && formik.touched.otp
                      ? 'red.500'
                      : 'gray.200'
                  }
                />

                {formik.touched.otp && formik.errors.otp && (
                  <Text color="red.500" fontSize="sm" mb="1">
                    {formik.errors.otp}
                  </Text>
                )}
              </FormControl>

              <FormControl
                isInvalid={
                  formik.touched.newPassword && formik.errors.newPassword
                }
              >
                <FormLabel
                  display="flex"
                  ms="4px"
                  fontSize="sm"
                  fontWeight="500"
                  color={textColor}
                  mb="8px"
                >
                  New Password<Text color="red.500">*</Text>
                </FormLabel>
                <Input
                  name="newPassword"
                  variant="auth"
                  fontSize="sm"
                  type="password"
                  placeholder="Enter new password"
                  mb="8px"
                  fontWeight="500"
                  size="lg"
                  value={formik.values.newPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  borderColor={
                    formik.errors.newPassword && formik.touched.newPassword
                      ? 'red.500'
                      : 'gray.200'
                  }
                />
                {formik.touched.newPassword && formik.errors.newPassword && (
                  <Text color="red.500" fontSize="sm" mb="1">
                    {formik.errors.newPassword}
                  </Text>
                )}
              </FormControl>

              <Button
                type="submit"
                fontSize="sm"
                variant="brand"
                fontWeight="500"
                w="100%"
                h="50"
                mt="24px"
                mb="12px"
              >
                {loading ? (
                  <Flex align="center" justify="center">
                    <Spinner size="sm" mr={2} /> Resetting...
                  </Flex>
                ) : (
                  'Reset Password'
                )}{' '}
              </Button>
            </form>
            <NavLink to="/auth/sign-in">
              <motion.div
                whileHover={{
                  scale: 1.1, // Move the arrow 5px to the right
                  transition: { duration: 0.3 },
                }}
                style={{ marginTop: '16px' }}
              >
                <Flex
                  fontSize="sm"
                  variant="outline"
                  fontWeight="500"
                  w="100%"
                  // h="50"
                  borderColor="gray.300"
                  align="center"
                  justify="center"
                  // p="10px"
                  _hover={{
                    cursor: 'pointer',
                    borderColor: 'gray.500',
                  }}
                >
                  <MdArrowBack
                    style={{ marginRight: '8px', marginBottom: '-1px' }}
                  />
                  Back to Login
                </Flex>
              </motion.div>
            </NavLink>
          </Flex>
        </Box>
      </Flex>
    </DefaultAuth>
  );
}

export default ResetPassword;
